import { useState } from "react";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { Check, ChevronsUpDown, Plus, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { CreateTeamDialog } from "@/components/teams/CreateTeamDialog";
import { useTeams } from "@/lib/api/teams.queries";
import { useActiveTeam } from "@/lib/team-context";
import { cn } from "@/lib/cn";

const ROLE_VARIANT: Record<string, "default" | "muted" | "secondary"> = {
  owner: "default",
  admin: "default",
  accountant: "secondary",
  cashier: "secondary",
  viewer: "muted",
};

/**
 * Header slot — three rendering modes:
 *   • zero teams        → primary "Crear equipo" CTA opening CreateTeamDialog
 *   • one team          → static label with role badge (no dropdown)
 *   • two or more teams → dropdown with checkmark on the active team and
 *                         "Crear equipo nuevo" item at the bottom
 */
export function TeamSwitcher() {
  const { t } = useTranslation();
  const teamsQuery = useTeams();
  const { activeTeam, setActiveTeamId } = useActiveTeam();
  const [createOpen, setCreateOpen] = useState(false);
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const handleSelect = (teamId: string) => {
    setActiveTeamId(teamId);
    // If we're inside `/companies/<id>/...`, the URL still points at a company
    // owned by the OLD team — keeping it would 403 on the next data fetch.
    // Bounce to the company list so the user picks a NIT in the new team.
    if (/^\/companies\/[^/]+/.test(pathname)) {
      navigate({ to: "/companies" });
    }
  };

  if (teamsQuery.isPending) {
    return (
      <Button variant="outline" size="sm" disabled className="hidden gap-2 sm:inline-flex">
        <span className="text-muted-foreground">{t("teams.switcher.loading")}</span>
      </Button>
    );
  }

  const teams = teamsQuery.data ?? [];

  if (teams.length === 0) {
    return (
      <>
        <Button
          variant="default"
          size="sm"
          className="hidden gap-2 sm:inline-flex"
          onClick={() => setCreateOpen(true)}
        >
          <Plus className="h-3.5 w-3.5" />
          {t("teams.switcher.create_first")}
        </Button>
        <CreateTeamDialog open={createOpen} onOpenChange={setCreateOpen} />
      </>
    );
  }

  if (teams.length === 1) {
    const only = teams[0]!;
    const variant = ROLE_VARIANT[only.role] ?? "muted";
    return (
      <Link
        to="/teams/$teamId"
        params={{ teamId: only.id }}
        className="hidden items-center gap-2 rounded-md border bg-background px-3 py-1.5 text-sm font-medium hover:bg-accent hover:text-accent-foreground sm:inline-flex"
      >
        <Users className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="max-w-[140px] truncate">{only.name}</span>
        <Badge variant={variant} className="text-[10px]">
          {t(`teams.role.${only.role}`)}
        </Badge>
      </Link>
    );
  }

  const current = activeTeam ?? teams[0]!;
  const variant = ROLE_VARIANT[current.role] ?? "muted";

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="hidden gap-2 sm:inline-flex"
            aria-label={t("teams.switcher.aria")}
          >
            <Users className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="max-w-[140px] truncate">{current.name}</span>
            <Badge variant={variant} className="text-[10px]">
              {t(`teams.role.${current.role}`)}
            </Badge>
            <ChevronsUpDown className="h-3.5 w-3.5 opacity-60" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64">
          <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
            {t("teams.switcher.label")}
          </DropdownMenuLabel>
          {teams.map((team) => {
            const isActive = team.id === current.id;
            return (
              <DropdownMenuItem
                key={team.id}
                onClick={() => handleSelect(team.id)}
                className="flex items-center gap-2"
              >
                <Check
                  className={cn(
                    "h-4 w-4",
                    isActive ? "opacity-100" : "opacity-0",
                  )}
                />
                <span className="flex-1 truncate">{team.name}</span>
                <Badge
                  variant={ROLE_VARIANT[team.role] ?? "muted"}
                  className="text-[10px]"
                >
                  {t(`teams.role.${team.role}`)}
                </Badge>
              </DropdownMenuItem>
            );
          })}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" />
            {t("teams.switcher.create_new")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <CreateTeamDialog open={createOpen} onOpenChange={setCreateOpen} />
    </>
  );
}
